import React, { useEffect, useState } from 'react';
import { X, Home, Building2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Ecran de bienvenue affiche au PREMIER login d'un locataire ou proprietaire.
 * - Logo anime (zoom + fade)
 * - Message personnalise avec {nom agence} + {prenom utilisateur}
 * - Liste de "Ce que vous pouvez faire ici"
 * - Stocke un flag `welcome_seen_{spaceKey}_{userId}` dans localStorage pour ne pas le rejouer
 *
 * Props:
 *   - spaceType: 'tenant' | 'owner'
 *   - userId: string (unique par utilisateur pour le storage key)
 *   - userName: string (prenom affiche)
 *   - agencyName: string
 *   - agencyLogoUrl: string | null
 *   - agencySlogan: string | null
 */
const WelcomeOverlay = ({ spaceType, userId, userName, agencyName, agencyLogoUrl, agencySlogan }) => {
  const storageKey = `welcome_seen_${spaceType}_${userId || 'anon'}`;
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const seen = localStorage.getItem(storageKey);
    if (!seen) {
      setVisible(true);
      // Trigger fade-in after mount
      requestAnimationFrame(() => setMounted(true));
    }
  }, [userId, storageKey]);

  const handleClose = () => {
    setMounted(false);
    setTimeout(() => {
      setVisible(false);
      localStorage.setItem(storageKey, new Date().toISOString());
    }, 300);
  };

  if (!visible) return null;

  const SpaceIcon = spaceType === 'owner' ? Building2 : Home;
  const spaceLabel = spaceType === 'owner' ? 'Espace Proprietaire' : 'Espace Locataire';

  const features = spaceType === 'owner'
    ? [
        { label: 'Consultez le solde de vos biens en temps reel' },
        { label: 'Demandez un retrait directement depuis l\'app' },
        { label: 'Suivez vos locataires, visites et documents' },
        { label: 'Telechargez vos releves financiers' },
      ]
    : [
        { label: 'Payez votre loyer en quelques clics (Mobile Money)' },
        { label: 'Consultez vos quittances et votre historique' },
        { label: 'Signalez une anomalie ou faites une demande' },
        { label: 'Accedez a vos contrats et etats des lieux' },
      ];

  const logoUrl = agencyLogoUrl
    ? (agencyLogoUrl.startsWith('http') ? agencyLogoUrl : `${process.env.REACT_APP_BACKEND_URL}${agencyLogoUrl}`)
    : null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}
      style={{
        background: 'linear-gradient(135deg, rgba(18,45,73,0.96) 0%, rgba(27,58,75,0.94) 100%)',
      }}
      data-testid="welcome-overlay"
    >
      {/* Particules dorees subtiles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 rounded-full bg-[#D4A745] opacity-60 animate-pulse" />
        <div className="absolute top-1/3 right-16 w-1.5 h-1.5 rounded-full bg-[#F2A900] opacity-70 animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-32 left-1/4 w-2.5 h-2.5 rounded-full bg-[#E8C56F] opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 right-20 w-1.5 h-1.5 rounded-full bg-[#D4A745] opacity-60 animate-pulse" style={{ animationDelay: '1.5s' }} />
      </div>

      <div
        className={`relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 ${mounted ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          data-testid="welcome-close-btn"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Hero avec logo + sparkle */}
        <div className="relative px-8 pt-10 pb-6 bg-brand-gradient text-white text-center">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gold-gradient" />
          <div className="inline-flex items-center justify-center relative">
            <span className="absolute inset-0 rounded-3xl bg-gold-gradient blur-xl opacity-50 animate-pulse" />
            <div className="relative w-24 h-24 rounded-3xl bg-white p-2 shadow-2xl ring-4 ring-[#D4A745]/40">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={agencyName}
                  className="w-full h-full object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-full h-full rounded-2xl bg-gold-gradient flex items-center justify-center">
                  <SpaceIcon className="w-10 h-10 text-[#1B3A4B]" />
                </div>
              )}
            </div>
          </div>
          <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-[#E8C56F] text-xs font-semibold tracking-wide uppercase">
            <Sparkles className="w-3 h-3" />
            {spaceLabel}
          </div>
          <h2 className="text-3xl font-bold mt-3 tracking-tight">
            Bienvenue{userName ? `, ${userName.split(' ')[0]}` : ''} !
          </h2>
          <p className="text-[#E8C56F] text-sm font-medium mt-1">chez {agencyName || "GAB'S SERVICES"}</p>
          {agencySlogan && (
            <p className="text-white/70 text-xs italic mt-2">"{agencySlogan}"</p>
          )}
        </div>

        {/* Features */}
        <div className="px-8 py-6 bg-white">
          <p className="text-xs text-[#1B3A4B]/60 uppercase tracking-wider font-semibold mb-3">Ce que vous pouvez faire ici</p>
          <ul className="space-y-2.5">
            {features.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-3 animate-fade-up"
                style={{ animationDelay: `${200 + i * 100}ms`, animationFillMode: 'both' }}
              >
                <div className="mt-0.5 w-5 h-5 rounded-full bg-gold-gradient-soft flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-[#1B3A4B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">{f.label}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={handleClose}
            className="w-full btn-brand rounded-xl h-11 mt-6 text-base"
            data-testid="welcome-cta-btn"
          >
            Commencer
          </Button>
          <p className="text-[11px] text-gray-400 text-center mt-3">Vous pouvez revoir ce message en videant le cache de votre navigateur.</p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeOverlay;
